import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { GoogleLogin } from "@react-oauth/google";
import CustomAlert from "./CustomAlert";
import "./SignUp.css";
import logo from "../assets/xtrafyls.svg";
import {
  google_icon,
  apple_icon,
  verify_email_icon,
  success_tick,
  emailIcon,
} from "../assets";
import { useNavigate } from "react-router-dom";
import {
  signupUser,
  verifyOtp,
  resendOtp,
  setVerificationEmail,
  clearError,
  resetOtpState,
  googleLogin,
  appleLogin,
} from "../features/auth/authSlice";

// Apple Login Configuration - Replace with actual values from Apple Developer Account
const APPLE_CLIENT_ID = "com.xtrafyl.web"; // TODO: Replace with actual Service ID
// const APPLE_REDIRECT_URI = "https://staging.xtrafyl.com/auth/apple/callback"; // Or specific callback staging URL
const APPLE_REDIRECT_URI = "https://xtrafyl.com/auth/apple/callback"; // Or specific callback prod URL


const validationSchema = Yup.object({
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required")
    .matches(
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      "Please enter a valid email address"
    ),
});

const SignUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isLoading, error, otpSent, verificationEmail, isAuthenticated } =
    useSelector((state) => state.auth);

  const [showOtpPopup, setShowOtpPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    type: "success",
    title: "",
    message: "",
  });

  const showCustomAlert = (type, title, message) => {
    setAlertConfig({ type, title, message });
    setShowAlert(true);
  };

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const otpRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ];

  const initialValues = {
    email: "",
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/create-password");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (otpSent) {
      setShowOtpPopup(true);
      setTimer(60);
      setCanResend(false);

      setTimeout(() => {
        if (otpRefs[0].current) {
          otpRefs[0].current.focus();
        }
      }, 300);
    }
  }, [otpSent]);

  useEffect(() => {
    if (showOtpPopup && timer > 0) {
      const countdown = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(countdown);
    } else if (timer === 0) {
      setCanResend(true);
    }
  }, [showOtpPopup, timer]);

  useEffect(() => {
    if (showOtpPopup || showSuccessPopup) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [showOtpPopup, showSuccessPopup]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        if (showSuccessPopup) {
          setShowSuccessPopup(false);
        }
        if (showOtpPopup) {
          handleCloseOtpPopup();
        }
      }
    };

    if (showOtpPopup || showSuccessPopup) {
      document.addEventListener("keydown", handleEsc);
      return () => document.removeEventListener("keydown", handleEsc);
    }
  }, [showOtpPopup, showSuccessPopup]);

  useEffect(() => {
    if (error) {
      showCustomAlert("error", "Sign Up Error", error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const resultAction = await dispatch(signupUser(values.email));

      if (signupUser.fulfilled.match(resultAction)) {
        dispatch(setVerificationEmail(values.email));
      }
    } catch (error) {
      // Signup error handled by Redux
    } finally {
      setSubmitting(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = pastedData.split("");
    setOtp([...newOtp, ...Array(6 - newOtp.length).fill("")]);

    const nextIndex = Math.min(pastedData.length, 5);
    otpRefs[nextIndex].current?.focus();
  };

  const handleVerifyOtp = async () => {
    const otpValue = otp.join("");

    if (otpValue.length !== 6) {
      showCustomAlert(
        "error",
        "Incomplete OTP",
        "Please enter complete 6-digit OTP"
      );
      return;
    }

    try {
      const resultAction = await dispatch(
        verifyOtp({ email: verificationEmail, otp: otpValue })
      );

      if (verifyOtp.fulfilled.match(resultAction)) {
        setShowOtpPopup(false);
        setShowSuccessPopup(true);

        setTimeout(() => {
          setShowSuccessPopup(false);
          navigate("/create-password");
        }, 2000);
      }
    } catch (error) {
      // OTP verification error handled by Redux
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    try {
      const resultAction = await dispatch(resendOtp(verificationEmail));

      if (resendOtp.fulfilled.match(resultAction)) {
        setOtp(["", "", "", "", "", ""]);
        setTimer(60);
        setCanResend(false);
        otpRefs[0].current?.focus();
        showCustomAlert("success", "OTP Sent", "OTP sent successfully!");
      }
    } catch (error) {
      // Resend OTP error handled by Redux
    }
  };

  const handleCloseOtpPopup = () => {
    setShowOtpPopup(false);
    setOtp(["", "", "", "", "", ""]);
    dispatch(resetOtpState());
  };

  const handlePostLoginNavigation = (resultPayload) => {
    const user = resultPayload?.User || resultPayload?.user;
    const profile = resultPayload?.Profile || resultPayload?.profile;

    const profileStep =
      user?.ProfileStep ||
      user?.profileStep ||
      profile?.ProfileStep ||
      profile?.profileStep;
    const basicInfoCompleted =
      user?.BasicInfoCompleted ??
      profile?.BasicInfoCompleted ??
      user?.basicInfoCompleted ??
      profile?.basicInfoCompleted ??
      false;
    const documentsCompleted =
      user?.DocumentsCompleted ??
      profile?.DocumentsCompleted ??
      user?.documentsCompleted ??
      profile?.documentsCompleted ??
      profile?.Documents ??
      false;
    const verificationStatus =
      profile?.VerificationStatus || profile?.verificationStatus || "unknown";

    if (profileStep === "set_password") {
      navigate("/create-password");
    } else if (!basicInfoCompleted) {
      navigate("/business-profile");
    } else if (!documentsCompleted) {
      navigate("/business-profile");
    } else if (basicInfoCompleted && documentsCompleted) {
      if (verificationStatus === "pending") {
        navigate("/verification-pending");
      } else if (
        verificationStatus === "verified" ||
        verificationStatus === "approved"
      ) {
        navigate("/dashboard");
      } else {
        navigate("/verification-pending");
      }
    } else {
      navigate("/dashboard");
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const resultAction = await dispatch(
        googleLogin(credentialResponse.credential)
      );

      if (googleLogin.fulfilled.match(resultAction)) {
        const user = resultAction.payload?.User || resultAction.payload?.user;
        if (user?.Email || user?.email) {
          localStorage.setItem("userEmail", user.Email || user.email);
        }
        handlePostLoginNavigation(resultAction.payload);
      }
    } catch (error) {
      showCustomAlert(
        "error",
        "Google Login Error",
        "Something went wrong. Please try again."
      );
    }
  };

  const handleGoogleError = () => {
    showCustomAlert(
      "error",
      "Google Login Failed",
      "Unable to login with Google. Please try again."
    );
  };

  const handleAppleLogin = async () => {
    try {
      // Load Apple JS SDK if not loaded
      if (!window.AppleID) {
        const script = document.createElement("script");
        script.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
        script.async = true;
        document.body.appendChild(script);
        await new Promise((resolve) => {
          script.onload = resolve;
        });
      }

      // Initialize Apple Sign In
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: "name email",
        redirectURI: APPLE_REDIRECT_URI,
        usePopup: true,
      });

      // Trigger Apple Sign In
      const response = await window.AppleID.auth.signIn();
      const { authorization, user } = response;

      const resultAction = await dispatch(
        appleLogin({
          idToken: authorization.id_token,
          authorizationCode: authorization.code,
          user: user || null,
        })
      );

      if (appleLogin.fulfilled.match(resultAction)) {
        const userData = resultAction.payload?.User || resultAction.payload?.user;
        if (userData?.Email || userData?.email) {
          localStorage.setItem("userEmail", userData.Email || userData.email);
        }

        // Check if it's a new user - redirect to registration flow
        const isNewUser = resultAction.payload?.IsNewUser ?? resultAction.payload?.isNewUser ?? resultAction.payload?.is_new_user ?? false;
        if (isNewUser) {
          navigate("/create-password");
          return;
        }

        handlePostLoginNavigation(resultAction.payload);
      } else if (appleLogin.rejected.match(resultAction)) {
        showCustomAlert(
          "error",
          "Apple Login Error",
          resultAction.payload || "Sign up failed. Please try again."
        );
      }
    } catch (error) {
      if (error.error !== "popup_closed_by_user") {
        showCustomAlert(
          "error",
          "Apple Login Failed",
          "Unable to sign up with Apple. Please try again."
        );
      }
    }
  };

  const isOtpComplete = otp.every((digit) => digit !== "");

  return (
    <div className="signup-container">
      <div className="signup-left">
        <div className="left-content">
          <img src={logo} alt="Xtrafyl" className="logo" />

          <div className="hero-section">
            <h1 className="hero-title">
              Fuel Your Business
              <br />
              With <span className="highlight">Xtrafyl.</span>
            </h1>
            <p className="hero-description text-big-body">
              From fuel delivery to charging, car wash, and more — we help you
              reach more customers, every day.
            </p>
          </div>
        </div>
      </div>

      <div className="signup-right">
        <div className="signup-right-content">
          <div className="form-header">
            <h2>Sign Up</h2>
            <p className="text-small-body">
              Join our network of providers delivering fuel, charging, and auto
              care on-demand.
            </p>
          </div>

          <div className="form-container">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              validateOnChange={true}
              validateOnBlur={true}
            >
              {({ errors, touched, isSubmitting, isValid, dirty }) => (
                <Form className="signup-form">
                  <div className="form-group signup_fm_group">
                    <label htmlFor="email" className="text-label">
                      Email Id
                    </label>
                    <div className="input-wrapper">
                      <img src={emailIcon} alt="" className="input-icon" />
                      <Field
                        type="email"
                        id="email"
                        name="email"
                        placeholder="Email Id"
                        className={`text-small-body ${
                          errors.email && touched.email ? "input-error" : ""
                        } ${
                          !errors.email && touched.email ? "input-success" : ""
                        }`}
                      />
                      {!errors.email && touched.email && (
                        <svg
                          className="input-success-icon"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M16.6667 5L7.50004 14.1667L3.33337 10"
                            stroke="#10B981"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="error-message"
                    />
                  </div>

                  <button
                    type="submit"
                    className="signup-button text-button"
                    disabled={isSubmitting || isLoading || !isValid || !dirty}
                  >
                    {isSubmitting || isLoading ? "SIGNING UP..." : "SIGN UP"}
                  </button>
                </Form>
              )}
            </Formik>

            <div className="divider">
              <span className="text-small-body">Or</span>
            </div>

            <div className="social-buttons">
              <div className="social-button-wrapper">
                <button className="social-button text-small-body-medium">
                  <img src={google_icon} alt="Google" className="social-icon" />
                  Continue with Google
                </button>
                <div className="google-login-overlay">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    useOneTap={false}
                    type="standard"
                    theme="outline"
                    size="large"
                    text="continue_with"
                    shape="rectangular"
                    width="300"
                  />
                </div>
              </div>
              <div className="social-button-wrapper">
                <button
                  className="social-button text-small-body-medium apple-login-btn"
                  onClick={handleAppleLogin}
                >
                  <img src={apple_icon} alt="Apple" className="social-icon" />
                  Continue with Apple
                </button>
              </div>
            </div>

            <p className="login-link text-small-body">
              Already have an account?{" "}
              <span
                onClick={() => navigate("/signin")}
                style={{ cursor: "pointer", color: "#6366F1", fontWeight: 600 }}
              >
                Login
              </span>
            </p>
          </div>
        </div>
      </div>

      {showOtpPopup && (
        <div className="popup-overlay" onClick={handleCloseOtpPopup}>
          <div
            className="popup-content otp-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="popup-close-btn" onClick={handleCloseOtpPopup}>
              ✕
            </button>

            <div className="popup-icon">
              <img
                src={verify_email_icon}
                alt="Verify Email"
                style={{ width: "120px", height: "120px" }}
              />
            </div>

            <h3 className="popup-title">Verify Your Email.</h3>
            <p className="popup-message">
              We've sent a 6-digit verification code to{" "}
              <strong>{verificationEmail}</strong>.<br />
              Enter the code below to verify your account and continue.
            </p>

            <div className="otp-inputs">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={otpRefs[index]}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={index === 0 ? handleOtpPaste : undefined}
                  className="otp-input"
                />
              ))}
            </div>

            <button
              className="verify-button"
              onClick={handleVerifyOtp}
              disabled={!isOtpComplete || isLoading}
            >
              {isLoading ? "VERIFYING..." : "VERIFY & CONTINUE"}
            </button>

            <div className="resend-section">
              <p className="resend-text">Didn't receive an OTP?</p>
              <button
                onClick={handleResend}
                className={`resend-link ${canResend ? "active" : "disabled"}`}
                disabled={!canResend || isLoading}
              >
                {canResend ? "Resend" : `Resend: ${formatTime(timer)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessPopup && (
        <div className="popup-overlay">
          <div
            className="popup-content success-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="popup-close-btn"
              onClick={() => setShowSuccessPopup(false)}
            >
              ✕
            </button>

            <div className="popup-icon">
              <img
                src={success_tick}
                alt="Success"
                style={{ width: "120px", height: "120px" }}
              />
            </div>

            <h3 className="popup-title">Verification Success</h3>
            <p className="popup-message">
              You will be redirected to password setup screen
            </p>
          </div>
        </div>
      )}

      <CustomAlert
        show={showAlert}
        onClose={() => setShowAlert(false)}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
      />
    </div>
  );
};

export default SignUp;
